import { computed, inject, Injectable, Signal } from '@angular/core';
import { IPolicy, IProcessProfile, IProfileParams } from '@partners/api';
import { ErrorsService } from '@partners/errors';
import { GLOBAL_SETTINGS } from '@partners/settings';
import { RootStore, ShellStore } from '@partners/state';
import { ThemeService } from '@partners/themes';
import { TConfigPartition, TLink, TProcessId, TTerminationReason } from '@partners/types';
import { hasQueryString, isEmpty, isNotEmpty, isValidUrl, queryStringToObject } from '@partners/utils';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { PolicyService } from './policy.service';

export type IProfileSanitizer = {
  [key in keyof IProfileParams as string]: string | number | boolean | null | undefined;
};

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private createEmitter: BehaviorSubject<IProcessProfile>;
  private changeEmitter: BehaviorSubject<IProcessProfile>;

  //#region public properties
  get change$(): Observable<IProcessProfile> {
    return this.changeEmitter.asObservable();
  }

  get create$(): Observable<IProcessProfile> {
    return this.createEmitter.asObservable();
  }

  get processId$$(): Signal<TProcessId | undefined> {
    return this._shellStore.processId$$;
  }

  get isProfileCreated(): boolean {
    return isNotEmpty(this._shellStore.profile$$()?.policy);
  }

  get profile$$(): Signal<IProcessProfile> {
    return this._shellStore.profile$$;
  }

  get params$$(): Signal<IProfileParams> {
    return computed(() => {
      const profile = this._shellStore.profile$$();
      if (!profile) {
        return undefined;
      }
      const { params } = profile;
      return params;
    });
  }

  get clubLabel$$(): Signal<string> {
    return computed(() => {
      const { name } = this.profile$$().partner;
      return name;
    });
  }

  get productLabel$$(): Signal<string> {
    return computed(() => {
      const { productName } = this._shellStore.globalConfig$$().text;
      return productName;
    });
  }

  matchCard = computed(() => this._shellStore.matchCard$$());
  /** match Account Frame Loan (הלוואה ממסגרת החשבון === הלוואה ללא כרטיס) */
  matchAcfl = computed(() => this._shellStore.matchAcfl$$());
  /** match any By Now Pay Later loan  */
  matchBnpl = computed(() => this._shellStore.matchBnpl$$());
  /** match any loan */
  matchLoan = computed(() => this._shellStore.matchLoan$$());

  //#endregion

  private readonly _shellStore = inject(ShellStore);
  private readonly _themeService = inject(ThemeService);
  private readonly _policyService = inject(PolicyService);
  private readonly _shellConfig = inject(GLOBAL_SETTINGS);
  private readonly _errorsService = inject(ErrorsService);
  private _rootStore = inject(RootStore);

  constructor() {
    this.createEmitter = new BehaviorSubject<IProcessProfile>(null);
    this.changeEmitter = new BehaviorSubject<IProcessProfile>(null);
  }

  terminateProcess(exitReason: TTerminationReason) {
    let exitUrl: TTerminationReason;

    // support redirect url like introduction & offer
    if (exitReason && isValidUrl(exitReason)) {
      exitUrl = exitReason;
    } else {
      let links = {} as TConfigPartition<TLink>;
      //const { links } = this._policyService.globalConfig$$();

      // A temporary solution until a mini policy will implemented
      /** check if there is already policy repository in the store */
      const hasPolicyInStore = this._shellStore.hasPolicy$$();
      if (hasPolicyInStore) {
        links = this._policyService.globalConfig$$().links;
      } else {
        const defaultUrl = 'https://www.cal-online.co.il/ecredit/loan-without-credit-card/' as TLink;
        links = { customerExitUrl: defaultUrl, errorUrl: defaultUrl, exitUrl: defaultUrl };
      }

      switch (exitReason) {
        case 'customer-opt-out': {
          exitUrl = links['customerExitUrl'];
          break;
        }
        case 'error': {
          exitUrl = links['errorUrl'];
          break;
        }
        case 'success': {
          exitUrl = links['exitUrl'];
          break;
        }
      }
    }
    if (exitUrl && isValidUrl(exitUrl)) {
      // reset store
      this._rootStore.dispatchResetState();
      // setTimeout in order to completed load summary call in case of I_0261
      setTimeout(() => {
        window.location.href = exitUrl;
      }, 100)
      return;
    }
    console.log(`terminating process... reason: ${exitReason}, url: ${exitUrl}`);
    this._errorsService.throwLocalError({ message: `terminate process ${exitReason} url was not found` });
  }

  /**
   * @description
   * creates an application profile for the current session. the profile is built from:
   * 1. the raw url params received as query string and parsed here
   * 2. a policy http response with baseline config & data for the specified process
   * 3. a custom theme css file downloaded asynchronously and inserted into the DOM
   *
   * the profile holds all the global application level data, and api which controls that
   * data. after a profile is created the app can be launched, not before.
   *
   * @param url the current browser's url
   * @returns an initialized new process profile stream
   */
  createAppProfile$(rawUrl: string = window.location.href): Observable<IProcessProfile> {
    return this.parseProfileParams(rawUrl).pipe(
      /** initialize the application with the url params received & parsed */
      filter((params: IProfileParams) => this.isValidParams(params)),
      switchMap(async (params: IProfileParams) => {
        let profile;
        if (!this.shouldCreateNewProfile(rawUrl, params)) {
          profile = await this.reloadProfile();
          this.changeEmitter.next(profile as IProcessProfile);
        } else {
          profile = await this.createProfile(params);
          this.createEmitter.next(profile as IProcessProfile);
        }
        return profile;
      })
    );
  }

  /**
   * @description
   * to survive a simple browser reload of the app, we can rely on session storage & our store
   * data to rehydrate the profile state, however to complete restoring the profile look |& feel
   * ui - we must pull the css file again each time the app reloads
   *
   * @returns a promise resolved with updated profile
   */
  private async reloadProfile(): Promise<IProcessProfile> {
    const profile = this.profile$$();
    await this.loadTheme(profile.params);
    return new Promise<IProcessProfile>((resolve) => {
      resolve(profile as IProcessProfile);
    });
  }

  /**
   * create and store a new profile data
   * @param profileParams the basic profile params (from browser url query string)
   * @returns a promise resolved with created profile
   */
  private async createProfile(profileParams: IProfileParams): Promise<IProcessProfile> {
    const { uid } = profileParams;

    this._shellStore.dispatchUpdateProfileParams(profileParams);

    if (uid) {
      return this.createWebProfile(profileParams);
    }

    return this.createDefaultProfile(profileParams);
  }

  private async createWebProfile(profileParams: IProfileParams): Promise<IProcessProfile> {
    if (!profileParams?.uid) {
      throwError(() => {
        message: `Can't create partner's profile without a uid`;
      });
    }

    /** load theme the new web profile */
    const theme = await this.loadTheme(profileParams);

    let nextProfile: Partial<IProcessProfile> = this.initializeProfile(profileParams);

    return this.finalizeProfile(nextProfile);
  }

  private sanitizeParams(obj: IProfileParams): IProfileParams {
    const ff: IProfileSanitizer = {};

    const result = Object.entries(obj).reduce((acc, [key, value]) => {
      ff[key] = value === 'null' || value === 'undefined' ? undefined : value;
      return ff;
    }, {} as IProfileSanitizer);

    return result as unknown as IProfileParams;
  }

  private async createDefaultProfile(profileParams: IProfileParams): Promise<IProcessProfile> {
    if (!profileParams?.token) {
      throwError(() => {
        message: `Can't create partner's profile without a token`;
      });
    }
    /** load policy data for the new profile */
    const policy = await this._policyService.loadPolicy(profileParams);

    /** load theme the new default profile */
    const theme = await this.loadTheme(profileParams);

    let nextProfile: Partial<IProcessProfile> = this.initializeProfile(profileParams, policy);

    return this.finalizeProfile(nextProfile);
  }

  private finalizeProfile(profile: Partial<IProcessProfile>): Promise<IProcessProfile> {
    /** save the new profile data to the store */
    this._shellStore.dispatchUpdateProcessProfile(profile as IProcessProfile);

    return new Promise<IProcessProfile>((resolve) => {
      resolve(profile as IProcessProfile);
    });
  }

  private initializeProfile(profileParams: IProfileParams, policy?: IPolicy): Partial<IProcessProfile> {
    return {
      partner: {
        name: profileParams.partner,
        description: profileParams.partner
      },
      params: profileParams,
      policy
    } as Partial<IProcessProfile>;
  }

  private async loadTheme({ theme }: IProfileParams) {
    return await this._themeService.loadTheme(theme);
  }

  /** profile should be created if:
   * first lunch
   * run new full url(with querystring) on current tab
   * also fix issue when user exit app and lunch again in order to override the old session store
   * "params" can comes from store in case of refresh, therefore we check queryparams via rawUrl
   */
  private shouldCreateNewProfile(rawUrl: string, params: IProfileParams): boolean {
    /** get current stored profile */
    const profile = this.profile$$();
    if (isEmpty(profile?.partner) || hasQueryString(rawUrl)) {
      return true;
    }
    /** check if new params requires updating the existing profile */
    return this.compareParams(params, profile?.params);
  }

  /**
   * @description
   * get the latest profile query string params either from url or store.
   * two events triggers this method:
   * 1. when the application is first loaded - its url contains query parameters from which we create a new
   * process profile
   * 2. when the application reloads - on browser refresh event the existing profile needs to be restored!
   * to do that - we read the profile query params from the store and recreate the latest profile
   *
   * @param url the current url of the app
   * @returns a stream of the profile query params
   */
  private parseProfileParams(url: string): Observable<IProfileParams> {
    return of(url).pipe(
      map((url) => {
        /** read whatever params are in the querystring */
        let urlParams = queryStringToObject(this._shellConfig.urlParams, url) as IProfileParams;

        /** just in case - eliminate ill-formed params (example: ...&theme="null" | &theme="undefined") */
        urlParams = { ...this.sanitizeParams(urlParams) };

        /** supplement url params with missing mandatory params (logic should be applied in server not client!) */
        return this.extendDefaultParams(urlParams);
      }),
      withLatestFrom(this._shellStore.params$),
      map(([nextParams, currentParams]: [IProfileParams, IProfileParams]) => {
        /** we have "next" params if this method is called on app first load */
        let hasNextParams = this.isValidParams(nextParams);
        /** we have "current" params if this method is called on browser refresh */
        let hasCurrentParams = this.isValidParams(currentParams);
        if (hasNextParams && !hasCurrentParams) {
          return nextParams;
        }
        if (!hasNextParams && hasCurrentParams) {
          return currentParams;
        }
        /** if we have both current & next - its time to update to next profile */
        if (this.compareParams(nextParams, currentParams)) {
          return nextParams;
        }
        return currentParams;
      })
    );
  }

  updateUrlParams(params: IProfileParams) {
    this._shellStore.dispatchUpdateProfileParams(params);
  }

  private isValidParams(params: IProfileParams): boolean {
    //ignoreNavigation equals true means that we will want to mark those parameters as invalid
    //so a new profile will created or updated.
    //ignoreNavigation equals true means that we will want to mark those parameters as invalid
    //so a new profile will created or updated.
    //example:
    //currently, the app's root page is completely reloaded after scan document indicates "success",
    //since this creates a new initialization scenario for the app (page refresh BUT we don't want to NITHER reload NOR create a new profile)
    //we use a "ignoreNavigation" query parameter in the URL to indicate this scenario - load app but do nothing with its current data or state
    if (params?.ignoreNavigation) {
      return false;
    }
    return Boolean(params?.token?.length || params?.uid?.length);
  }

  private compareParams = (nextParams: IProfileParams, currentParams: IProfileParams): boolean => {
    if (nextParams?.uid) {
      return nextParams?.uid !== currentParams?.uid;
    }

    return nextParams?.token !== currentParams?.token;
  };

  /**
   * @description
   * append some default constant params & values to the query string, these params are required
   * by the policy service to complete
   * @param targetObject the newly created profile object
   * @param extensionNode the data node representing key+value
   */
  private extendDefaultParams(targetObject: Partial<IProfileParams>): IProfileParams {
    /** by default, set trigger code 0 (not null) */
    const triggerCode = targetObject?.triggerCode ? targetObject.triggerCode : 0;

    /**
     * as default - we use the "partner" url param value as the theme name. however - if the "theme"
     * url param has a value - we prefer that value as the theme name. to keep "theme" as the SSR of
     * the app's theme name - we match the "partner" value as the theme name if "theme" is empty.
     **/
    const theme = isEmpty(targetObject?.theme) ? targetObject?.partner : targetObject?.theme;

    return Object.assign({}, { ...targetObject }, { theme, triggerCode }) as IProfileParams;
  }
}
